import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { teamAPI } from '../services/api';

const ROL_LABELS = {
  admin: 'Administrador',
  superadmin: 'Super Admin',
  operador: 'Operador',
  visor: 'Visor',
};

const ROL_COLORS = {
  admin: 'bg-blue-900/40 text-blue-400',
  superadmin: 'bg-purple-900/40 text-purple-400',
  operador: 'bg-green-900/40 text-green-400',
  visor: 'bg-amber-900/40 text-amber-400',
};

const TeamPage = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', nombre: '', rol: 'operador' });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  const loadTeam = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await teamAPI.getTeam(user.id);
      setTeam(res.data);
    } catch {
      setTeam(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setMessage(null);
    try {
      const res = await teamAPI.inviteUser(user.id, inviteData);
      setMessage({ type: 'success', text: res.data.message });
      setInviteData({ email: '', nombre: '', rol: 'operador' });
      setShowInvite(false);
      loadTeam();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error al enviar invitacion' });
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async (memberId, memberEmail) => {
    if (!window.confirm(`Eliminar a ${memberEmail} del equipo?`)) return;
    try {
      await teamAPI.removeMember(user.id, memberId);
      setMessage({ type: 'success', text: `${memberEmail} eliminado del equipo` });
      loadTeam();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error al eliminar' });
    }
  };

  const handleCancelInvite = async (token) => {
    try {
      await teamAPI.cancelInvitation(user.id, token);
      setMessage({ type: 'success', text: 'Invitacion cancelada' });
      loadTeam();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Equipo</h1>
          <p className="text-sm text-gray-500 mt-1">
            {team?.total_usuarios || 0} de {team?.limite === -1 ? 'ilimitados' : team?.limite} usuarios · Plan {team?.plan?.toUpperCase()}
          </p>
        </div>
        {isAdmin && team?.puede_invitar && (
          <button
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition"
            data-testid="invite-user-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Invitar Usuario
          </button>
        )}
        {isAdmin && !team?.puede_invitar && team?.limite !== -1 && (
          <p className="text-sm text-amber-600 font-medium">
            Limite de usuarios alcanzado ({team?.total_usuarios}/{team?.limite})
          </p>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`px-4 py-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`} data-testid="team-message">
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-2 font-bold">x</button>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" data-testid="invite-modal">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Invitar Usuario al Equipo</h2>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={inviteData.nombre}
                  onChange={(e) => setInviteData({ ...inviteData, nombre: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="Nombre del usuario"
                  data-testid="invite-nombre"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="email@ejemplo.com"
                  data-testid="invite-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rol / Permisos</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setInviteData({ ...inviteData, rol: 'operador' })}
                    className={`p-3 rounded-lg border-2 text-left transition ${
                      inviteData.rol === 'operador'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="rol-operador"
                  >
                    <p className="font-semibold text-sm text-gray-900">Operador</p>
                    <p className="text-xs text-gray-500 mt-1">Acceso completo. Puede registrar pesajes, servicios, eventos.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setInviteData({ ...inviteData, rol: 'visor' })}
                    className={`p-3 rounded-lg border-2 text-left transition ${
                      inviteData.rol === 'visor'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="rol-visor"
                  >
                    <p className="font-semibold text-sm text-gray-900">Visor</p>
                    <p className="text-xs text-gray-500 mt-1">Solo lectura. Ve dashboard, reportes y alertas.</p>
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={sending}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 transition"
                  data-testid="send-invite-btn"
                >
                  {sending ? 'Enviando...' : 'Enviar Invitacion'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowInvite(false); setMessage(null); }}
                  className="px-4 py-2.5 text-gray-600 hover:text-gray-900 text-sm transition"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Team Members */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="team-members">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Miembros del Equipo</h2>
        </div>
        <div className="divide-y divide-gray-50">
          {team?.miembros?.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50" data-testid={`member-${m.email}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-sm">
                  {(m.nombre || m.email || '?')[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{m.nombre || m.email}</p>
                  <p className="text-xs text-gray-500">{m.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${ROL_COLORS[m.rol] || 'bg-gray-100 text-gray-600'}`}>
                  {ROL_LABELS[m.rol] || m.rol}
                </span>
                {isAdmin && m.id !== user?.id && m.rol !== 'superadmin' && (
                  <button
                    onClick={() => handleRemove(m.id, m.email)}
                    className="text-red-400 hover:text-red-600 transition p-1"
                    title="Eliminar del equipo"
                    data-testid={`remove-${m.email}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending Invitations */}
      {team?.invitaciones_pendientes?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="pending-invitations">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Invitaciones Pendientes</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {team.invitaciones_pendientes.map((inv) => (
              <div key={inv.token} className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="font-medium text-gray-700 text-sm">{inv.nombre} <span className="text-gray-400">({inv.email})</span></p>
                  <p className="text-xs text-gray-400 mt-0.5">Rol: {ROL_LABELS[inv.rol] || inv.rol} · Enviada: {inv.fecha_creacion?.substring(0, 10)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-amber-50 text-amber-600 rounded-full text-xs font-medium">Pendiente</span>
                  {isAdmin && (
                    <button
                      onClick={() => handleCancelInvite(inv.token)}
                      className="text-gray-400 hover:text-red-500 text-xs transition"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Role Legend */}
      <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
        <h3 className="font-semibold text-gray-800 mb-3 text-sm">Permisos por Rol</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div className="space-y-1">
            <p className="font-medium text-green-700">Operador (Acceso Completo)</p>
            <ul className="text-gray-600 space-y-0.5 ml-3">
              <li>- Ve dashboard, alertas, reportes, analytics</li>
              <li>- Registra pesajes, servicios, pariciones, eventos</li>
              <li>- Usa el asistente IA</li>
              <li>- NO puede invitar usuarios ni cambiar plan</li>
            </ul>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-amber-700">Visor (Solo Lectura)</p>
            <ul className="text-gray-600 space-y-0.5 ml-3">
              <li>- Ve dashboard, alertas, reportes, analytics</li>
              <li>- NO puede registrar, editar ni eliminar nada</li>
              <li>- Ideal para inversores, socios, veterinarios</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
